import React, { useState } from 'react';
import ColorPicker from './ColorPicker';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RichTextEditor from './RichTextEditor';
import CustomTableEditor from './CustomTableEditor';
import FindingsWalkthroughConfig from './FindingsWalkthroughConfig';
import OwaspFindingsConfig from './OwaspFindingsConfig';
import { Input } from '../Common';
import { FONT_OPTIONS_INHERIT } from './fontOptions';
import { METRIC_TILE_LABEL_GAP } from './metricTileDefaults';

const COMPONENT_TYPES = [
  { type: 'heading', label: 'Heading', icon: 'H' },
  { type: 'text-block', label: 'Text', icon: '\u00b6' },
  { type: 'custom-table', label: 'Table', icon: '\u25a6' },
  { type: 'image', label: 'Image', icon: '\ud83d\uddbc\ufe0f' },
  { type: 'severity-chart', label: 'Chart', icon: '\ud83e\udd67' },
  { type: 'horizontal-line', label: 'Line', icon: '─' },
  { type: 'spacer', label: 'Space', icon: '↕' },
  { type: 'version-history', label: 'Versions', icon: '🕑' },
  { type: 'metric-tiles', label: 'Tiles', icon: '▤' },
  { type: 'findings-walkthrough', label: 'Walkthrough', icon: '🧾' },
  { type: 'owasp-findings', label: 'OWASP', icon: '🛡️' },
  { type: 'revalidation-summary', label: 'Revalidation', icon: '🔁' },
];

function ComponentConfig({ comp, sectionId, dispatch }) {
  const { type, config } = comp;
  const update = (key, value) => {
    dispatch({ type: 'UPDATE_COMPONENT', payload: { sectionId, componentId: comp.id, updates: { [key]: value } } });
  };

  // Several fields in one dispatch. Three sequential update() calls would be
  // three reducer passes over the same component, and the later ones would be
  // built from state the earlier ones have not landed in yet.
  const updateMany = (updates) => {
    dispatch({ type: 'UPDATE_COMPONENT', payload: { sectionId, componentId: comp.id, updates } });
  };

  switch (type) {
    case 'heading':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Level:</span>
              {[1, 2, 3].map(lvl => (
                <button key={lvl} onClick={() => update('level', lvl)} style={{
                  padding: '2px 10px', fontSize: 11, fontWeight: 600,
                  background: config.level === lvl ? 'var(--primary)' : 'var(--bg-input)',
                  color: config.level === lvl ? '#fff' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
                }}>H{lvl}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Align:</span>
              {['left', 'center', 'right'].map(a => (
                <button key={a} onClick={() => update('align', a)} style={{
                  padding: '2px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                  background: (config.align || 'left') === a ? 'var(--primary)' : 'var(--bg-input)',
                  color: (config.align || 'left') === a ? '#fff' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
                }}>{a}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Colour:</span>
              {/* Applies to the whole heading on both the plain and the rich-text
                  branch, so it keeps working after someone types in the editor
                  below. Colour set on a selection inside the heading still wins. */}
              <ColorPicker label="Heading colour" size={20}
                value={config.color || null} onChange={c => update('color', c)} />
            </div>
          </div>
          <RichTextEditor content={config.html || config.text || ''} onChange={html => update('html', html)} />
        </div>
      );

    case 'text-block':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Font:</span>
            <select value={config.font || ''} onChange={e => update('font', e.target.value || null)}
              style={{ padding: '2px 6px', fontSize: 11, borderRadius: 'var(--radius)',
                       border: '1px solid var(--border)', background: 'var(--bg-input)',
                       color: 'var(--text)', cursor: 'pointer' }}>
              {FONT_OPTIONS_INHERIT.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Font Size:</span>
            <select
              value={config.fontSize || ''}
              onChange={e => update('fontSize', e.target.value ? Number(e.target.value) : null)}
              style={{
                padding: '2px 6px', fontSize: 11, borderRadius: 'var(--radius)',
                border: '1px solid var(--border)', background: 'var(--bg-input)',
                color: 'var(--text)', cursor: 'pointer'
              }}
            >
              <option value="">Default</option>
              <option value="8">8pt</option>
              <option value="9">9pt</option>
              <option value="10">10pt</option>
              <option value="11">11pt</option>
              <option value="12">12pt</option>
              <option value="13">13pt</option>
              <option value="14">14pt</option>
              <option value="16">16pt</option>
              <option value="18">18pt</option>
              <option value="20">20pt</option>
              <option value="24">24pt</option>
            </select>
            {/* A block background. Paragraph shading in the DOCX; a drawn band
                in the PDF. Clearing it returns the block to no background at
                all rather than to white, which matters on a coloured page. */}
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Background:</span>
            <ColorPicker label="Text background" size={18}
              value={config.backgroundColor || null}
              onChange={c => update('backgroundColor', c)} />
            {config.backgroundColor && (
              <button type="button" onClick={() => update('backgroundColor', null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
                         color: 'var(--text-secondary)', textDecoration: 'underline', padding: 0 }}>Clear</button>
            )}
          </div>
          <RichTextEditor content={config.html || ''} onChange={html => update('html', html)} />
        </div>
      );

    case 'custom-table':
      return (
        <CustomTableEditor
          columns={config.columns || []}
          rows={config.rows || []}
          rowStyles={config.rowStyles || []}
          tableStyle={config.tableStyle || {}}
          cellStyles={config.cellStyles || []}
          columnStyles={config.columnStyles || []}
          onChange={(columns, rows, rowStyles, tableStyle, cellStyles, columnStyles) => {
            dispatch({
              type: 'UPDATE_COMPONENT',
              payload: { sectionId, componentId: comp.id, updates: { columns, rows, rowStyles, tableStyle, cellStyles, columnStyles } }
            });
          }}
        />
      );

    case 'image': {
      const handleImageUpload = (ev) => {
        const file = ev.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
          alert('Image is larger than 2 MB. Please use a smaller image.');
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const data = reader.result;
          // The image's own proportions, measured here because the browser
          // already has it decoded and nothing downstream does. Without them
          // the Word renderer has no aspect ratio to work from and falls back
          // to assuming 4:3, which visibly squashes a screenshot.
          const probe = new window.Image();
          probe.onload = () => {
            // Prefill the boxes with the image's real size, shrunk to fit the
            // 600px text width if it is bigger. A screenshot is usually wider
            // than the page, and defaulting to its raw pixel width would put
            // an overflowing number in a box the user then has to correct.
            const nW = probe.naturalWidth || 0;
            const nH = probe.naturalHeight || 0;
            // Fit both axes: a tall screenshot overruns the page height
            // long before it overruns the width.
            const fit = Math.min(1, nW ? 600 / nW : 1, nH ? 900 / nH : 1);
            updateMany({
              imageData: data,
              imageNaturalWidth: nW || null,
              imageNaturalHeight: nH || null,
              imageWidthPx: nW ? Math.max(10, Math.round(nW * fit)) : null,
              imageHeightPx: nH ? Math.max(10, Math.round(nH * fit)) : null,
            });
          };
          // A format the browser cannot decode still uploads; it just has no
          // measured ratio, and the renderer keeps its old fallback.
          probe.onerror = () => updateMany({ imageData: data,
                                             imageNaturalWidth: null, imageNaturalHeight: null });
          probe.src = data;
        };
        reader.readAsDataURL(file);
      };

      const natW = Number(config.imageNaturalWidth) || null;
      const natH = Number(config.imageNaturalHeight) || null;
      // Fall back to whatever the old percentage produced, so a template saved
      // before these boxes existed opens showing the size it already renders at
      // rather than a number nobody chose. 600px is the renderer's full
      // text-width reference.
      const legacyPct = Math.min(100, Math.max(10, Number(config.width) || 80));
      const pxW = Number(config.imageWidthPx) || Math.round(600 * legacyPct / 100);
      const pxH = Number(config.imageHeightPx)
               || Math.round(pxW * ((natW && natH) ? natH / natW : 0.75));
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input type="file" accept="image/*" onChange={handleImageUpload} style={{ fontSize: 11 }} />
          {config.imageData && (
            <div style={{ position: 'relative', maxWidth: 160 }}>
              <img src={config.imageData} alt="" style={{ maxWidth: '100%', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }} />
              <button onClick={() => update('imageData', null)} style={{
                position: 'absolute', top: 2, right: 2, background: '#ef4444', color: '#fff',
                border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer',
                fontSize: 11, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>x</button>
            </div>
          )}
          <Input label="Caption" value={config.caption || ''} onChange={e => update('caption', e.target.value)} style={{ marginBottom: 0 }} />

          {/* Size in pixels, the same two boxes the header image config uses,
              because that is the control people already know here. They are
              independent, as the header's are: Word is given exactly these
              dimensions. "Fit to source" is next to them because independent
              boxes are how an image gets squashed -- it recomputes the height
              from the width using the image's own measured proportions. */}
          <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
            <Input label="W (px)" type="number" min="10" max="600" value={pxW}
              onChange={e => update('imageWidthPx', parseInt(e.target.value, 10) || null)}
              style={{ marginBottom: 0, flex: 1 }} />
            <Input label="H (px)" type="number" min="10" max="900" value={pxH}
              onChange={e => update('imageHeightPx', parseInt(e.target.value, 10) || null)}
              style={{ marginBottom: 0, flex: 1 }} />
            {natW && natH && (
              <button type="button"
                onClick={() => updateMany({ imageWidthPx: pxW,
                                            imageHeightPx: Math.max(10, Math.round(pxW * natH / natW)) })}
                title={`Source is ${natW}×${natH}px`}
                style={{ padding: '4px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                         background: 'var(--bg-input)', color: 'var(--text)', whiteSpace: 'nowrap',
                         border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                Fit to source
              </button>
            )}
          </div>
          {natW && natH && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {natW}&times;{natH}px source. {Math.abs(pxW * natH / natW - pxH) > 2
                ? 'Current size is not in proportion.'
                : 'In proportion.'}
            </span>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Align:</span>
            {['left', 'center', 'right'].map(a => (
              <button key={a} onClick={() => update('align', a)} style={{
                padding: '2px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                background: (config.align || 'center') === a ? 'var(--primary)' : 'var(--bg-input)',
                color: (config.align || 'center') === a ? '#fff' : 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
              }}>{a}</button>
            ))}
          </div>
        </div>
      );
    }

    case 'severity-chart':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Chart:</span>
          {['pie', 'bar'].map(ct => (
            <button key={ct} onClick={() => update('chartType', ct)} style={{
              padding: '2px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
              background: config.chartType === ct ? 'var(--primary)' : 'var(--bg-input)',
              color: config.chartType === ct ? '#fff' : 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
            }}>{ct}</button>
          ))}
        </div>
      );

    case 'spacer':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Height:</span>
          <input type="number" min={1} max={400} step={1} value={config.height ?? 20}
            onChange={e => update('height', Math.min(400, Math.max(1, Number(e.target.value) || 1)))}
            style={{ width: 66, padding: '2px 6px', fontSize: 11, borderRadius: 'var(--radius)',
                     border: '1px solid var(--border)', background: 'var(--bg-input)',
                     color: 'var(--text)', fontFamily: 'inherit' }} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>pt</span>
          <input type="range" min={1} max={200} value={Math.min(200, config.height ?? 20)}
            onChange={e => update('height', Number(e.target.value))}
            style={{ flex: 1, minWidth: 70, maxWidth: 150 }} />
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            ~{((((config.height ?? 20)) / 72) * 25.4).toFixed(0)}mm
          </span>
        </div>
      );

    case 'findings-walkthrough':
      return <FindingsWalkthroughConfig config={config} update={update} />;
    case 'owasp-findings':
      return <OwaspFindingsConfig config={config} update={update} />;

    case 'metric-tiles': {
      const tiles = Array.isArray(config.tiles) ? config.tiles : [];
      const setTiles = next => update('tiles', next);
      const setTile = (i, k, v) => setTiles(tiles.map((t, n) => n === i ? { ...t, [k]: v } : t));
      const addTile = () => setTiles([...tiles, { label: 'Label', value: '', bg: '#f3f4f6',
                                                  labelColor: '#666666', valueColor: '#1f3864' }]);
      const move = (i, d) => {
        const n = i + d;
        if (n < 0 || n >= tiles.length) return;
        const next = tiles.slice();
        [next[i], next[n]] = [next[n], next[i]];
        setTiles(next);
      };
      const inp = {
        padding: '2px 6px', fontSize: 11, borderRadius: 'var(--radius)', boxSizing: 'border-box',
        border: '1px solid var(--border)', background: 'var(--bg-input)',
        color: 'var(--text)', fontFamily: 'inherit', minWidth: 0,
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input value={config.heading || ''} placeholder="Heading &mdash; blank for none"
            onChange={e => update('heading', e.target.value)} style={{ ...inp, width: '100%%' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Per row:</span>
            <input type="number" min={1} max={8} value={config.perRow || tiles.length || 1}
              title="How many tiles sit on one line before wrapping"
              onChange={e => update('perRow', Math.max(1, Number(e.target.value) || 1))}
              style={{ ...inp, width: 56 }} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Label:</span>
            <input type="number" min={5} max={20} value={config.labelSize || 8}
              onChange={e => update('labelSize', Number(e.target.value) || 8)} style={{ ...inp, width: 52 }} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Value:</span>
            <input type="number" min={6} max={48} value={config.valueSize || 16}
              onChange={e => update('valueSize', Number(e.target.value) || 16)} style={{ ...inp, width: 52 }} />
            {/* ?? and not ||: an author who wants the caption tight against
                the figure stores 0, and || would show it back as the default. */}
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Gap:</span>
            <input type="number" min={0} max={24} value={config.labelGap ?? METRIC_TILE_LABEL_GAP}
              title="Points between the caption and the figure"
              onChange={e => update('labelGap', Math.min(24, Math.max(0, Number(e.target.value) || 0)))}
              style={{ ...inp, width: 52 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11,
                            color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={config.upperCaseLabels !== false}
                onChange={e => update('upperCaseLabels', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
              Caps
            </label>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {tiles.length === 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                No tiles yet. A tile is a caption over a figure &mdash; the value takes report variables.
              </div>
            )}
            {tiles.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
                                    padding: 5, borderRadius: 'var(--radius)',
                                    border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                <input value={t.label || ''} placeholder="Label"
                  onChange={e => setTile(i, 'label', e.target.value)} style={{ ...inp, flex: '1 1 90px' }} />
                <input value={t.value || ''} placeholder="Value or %CRITICALCOUNT%"
                  onChange={e => setTile(i, 'value', e.target.value)} style={{ ...inp, flex: '1 1 120px' }} />
                <ColorPicker label="Tile background" size={18} value={t.bg || null}
                  onChange={c => setTile(i, 'bg', c)} />
                <ColorPicker label="Label colour" size={18} allowNone={false}
                  value={t.labelColor || '#666666'} onChange={c => setTile(i, 'labelColor', c || '#666666')} />
                <ColorPicker label="Value colour" size={18} allowNone={false}
                  value={t.valueColor || '#1f3864'} onChange={c => setTile(i, 'valueColor', c || '#1f3864')} />
                <button type="button" title="Move left" onClick={() => move(i, -1)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0 2px' }}>&lsaquo;</button>
                <button type="button" title="Move right" onClick={() => move(i, 1)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0 2px' }}>&rsaquo;</button>
                <button type="button" title="Remove" onClick={() => setTiles(tiles.filter((_, n) => n !== i))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 15, padding: '0 3px' }}>&times;</button>
              </div>
            ))}
            <button type="button" onClick={addTile}
              style={{ alignSelf: 'flex-start', padding: '3px 9px', fontSize: 11, fontWeight: 600,
                       borderRadius: 'var(--radius)', background: 'var(--bg-input)',
                       color: 'var(--text-secondary)', border: '1px solid var(--border)',
                       cursor: 'pointer', fontFamily: 'inherit' }}>
              + Tile
            </button>
          </div>
        </div>
      );
    }

    case 'version-history': {
      const ts = config.tableStyle || {};
      const setTs = (k, v) => update('tableStyle', { ...ts, [k]: v });
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input value={config.heading || ''} placeholder="Heading — blank for none"
            onChange={e => update('heading', e.target.value)}
            style={{ width: '100%', padding: '3px 7px', fontSize: 11, borderRadius: 'var(--radius)',
                     boxSizing: 'border-box', border: '1px solid var(--border)',
                     background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit' }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
                          color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={config.allTemplates === true}
              onChange={e => update('allTemplates', e.target.checked)}
              style={{ cursor: 'pointer', margin: 0 }} />
            Every report for this engagement, not just this template
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <ColorPicker value={ts.headerBg || '#f3f4f6'} size={20} label="Header background"
              onChange={v => setTs('headerBg', v || '#f3f4f6')} />
            <ColorPicker value={ts.headerTextColor || '#000000'} size={20} label="Header text"
              onChange={v => setTs('headerTextColor', v || '#000000')} />
            <ColorPicker value={ts.borderColor || '#e5e7eb'} size={20} label="Borders"
              onChange={v => setTs('borderColor', v || '#e5e7eb')} />
            <select value={ts.font || ''} onChange={e => setTs('font', e.target.value || null)}
              title="Table font"
              style={{ padding: '2px 5px', fontSize: 10, borderRadius: 'var(--radius)',
                       border: '1px solid var(--border)', background: 'var(--bg-input)',
                       color: 'var(--text)', cursor: 'pointer' }}>
              {FONT_OPTIONS_INHERIT.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
                            color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={ts.showHeader !== false}
                onChange={e => setTs('showHeader', e.target.checked)}
                style={{ cursor: 'pointer', margin: 0 }} />
              Column header
            </label>
          </div>
        </div>
      );
    }

    case 'horizontal-line':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Color:</span>
            <ColorPicker label="Line colour" size={20}
              value={config.color || '#cccccc'}
              onChange={c => update('color', c || '#cccccc')} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Thickness:</span>
            <input type="range" min={1} max={10} value={config.thickness || 1}
              onChange={e => update('thickness', Number(e.target.value))}
              style={{ width: 60 }} />
            <span style={{ fontSize: 11, fontWeight: 600 }}>{config.thickness || 1}px</span>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Width:</span>
            <input type="range" min={10} max={100} value={config.width || 100}
              onChange={e => update('width', Number(e.target.value))}
              style={{ width: 60 }} />
            <span style={{ fontSize: 11, fontWeight: 600 }}>{config.width || 100}%</span>
          </div>
          <div style={{ borderTop: `${config.thickness || 1}px solid ${config.color || '#cccccc'}`, width: `${config.width || 100}%`, margin: '0 auto' }} />
        </div>
      );

    default:
      return null;
  }
}

function SortableComponent({ comp, sectionId, dispatch }) {
  const [collapsed, setCollapsed] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: comp.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: 4,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        background: 'var(--bg)', overflow: 'hidden',
      }}>
        {/* Component header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
          background: 'var(--bg-input)', borderBottom: collapsed ? 'none' : '1px solid var(--border)',
          cursor: 'pointer', userSelect: 'none', fontSize: 12
        }}>
          <div {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--text-secondary)', fontSize: 14 }}>
            ≡
          </div>
          <span style={{ fontWeight: 600, flex: 1 }} onClick={() => setCollapsed(!collapsed)}>
            {comp.label || comp.type}
          </span>
          <button onClick={() => setCollapsed(!collapsed)} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)',
            fontSize: 12, padding: '1px 4px', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
            transition: 'transform 0.2s'
          }}>&#9660;</button>
          <button onClick={() => dispatch({ type: 'MOVE_COMPONENT_OUT', payload: { sourceSectionId: sectionId, componentId: comp.id } })} style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: 'var(--text-secondary)',
            fontSize: 10, padding: '1px 5px', lineHeight: 1
          }} title="Move out to main canvas">↗</button>
          <button onClick={() => dispatch({ type: 'REMOVE_COMPONENT', payload: { sectionId, componentId: comp.id } })} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444',
            fontSize: 14, padding: '1px 4px', lineHeight: 1
          }} title="Remove">&#10005;</button>
        </div>
        {/* Component config */}
        {!collapsed && (
          <div style={{ padding: 8 }}>
            <ComponentConfig comp={comp} sectionId={sectionId} dispatch={dispatch} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function CompositeSection({ sectionId, components, dispatch }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = components.findIndex(c => c.id === active.id);
    const newIndex = components.findIndex(c => c.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      dispatch({
        type: 'REORDER_COMPONENTS',
        payload: { sectionId, components: arrayMove(components, oldIndex, newIndex) }
      });
    }
  };

  return (
    <div>
      {/* Inline palette */}
      <div style={{
        display: 'flex', gap: 4, flexWrap: 'wrap', padding: '6px 0',
        borderBottom: '1px solid var(--border)', marginBottom: 8
      }}>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', alignSelf: 'center', marginRight: 4 }}>Add:</span>
        {COMPONENT_TYPES.map(ct => (
          <button
            key={ct.type}
            onClick={() => dispatch({
              type: 'ADD_COMPONENT',
              payload: { sectionId, componentType: ct.type, label: ct.label }
            })}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
              background: 'var(--bg-input)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 11,
              color: 'var(--text)', transition: 'all 0.15s'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--primary)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            <span>{ct.icon}</span>
            <span>{ct.label}</span>
          </button>
        ))}
      </div>

      {/* Sortable components */}
      {components.length === 0 ? (
        <div style={{
          padding: 20, textAlign: 'center', border: '2px dashed var(--border)',
          borderRadius: 'var(--radius)', color: 'var(--text-secondary)', fontSize: 12
        }}>
          Click a button above to add components to this section.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={components.map(c => c.id)} strategy={verticalListSortingStrategy}>
            {components.map(comp => (
              <SortableComponent key={comp.id} comp={comp} sectionId={sectionId} dispatch={dispatch} />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
